import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { analytics, WrapperStyles, getUsername } from "../../shared/utils";
import type { FBTeam, FBUser } from "../../shared/types";
import { fetchUser, fetchTeam, fetchTeamOwner } from "../../shared/services";
import DownloadOverlay from "../overlays/DownloadOverlay";
import FooterOverlay from "../overlays/FooterOverlay";
import LoadingOverlay from "../overlays/LoadingOverlay";
import DynamicMeta from "../shared/DynamicMeta";

const PublicTeamView = () => {
  const { id: teamId } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [team, setTeam] = useState<FBTeam>();
  const [user, setUser] = useState<FBUser>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch referrer id if passed
  const [searchParams] = useSearchParams();
  const referrerId = searchParams.get("referrerId");

  useEffect(() => {
    analytics.screen("Web/Public/Team", {
      teamId: teamId,
      referrerId: referrerId,
    });
  }, [teamId, referrerId]);

  useEffect(() => {
    if (!teamId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // Conditionally fetch user based on referrerId
    const userPromise = referrerId
      ? fetchUser(referrerId)
      : fetchTeamOwner(teamId);

    Promise.all([fetchTeam(teamId), userPromise])
      .then(([team, user]) => {
        setTeam(team);
        setUser(user);
      })
      .catch((error) => {
        console.error(error);
        setUser(undefined);
        setTeam(undefined);
      })
      .finally(() => setLoading(false));
  }, [teamId, referrerId]);

  const TeamOverlay = () => {
    if (!user || !team) return null;
    const name = getUsername(user);
    return (
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          gap: 1,
          width: "100% !important",
          borderRadius: 2,
        }}
      >
        <CardContent
          sx={{
            flex: "1 0 auto",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "row",
            backgroundColor: "rgba(0, 0, 0, 0.87)",
            borderRadius: 2,
            width: "100% !important",
          }}
        >
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {user.profilePicture && (
              <Avatar
                src={user.profilePicture}
                alt={name}
                sx={{ width: 48, height: 48 }}
              />
            )}
            <Typography
              sx={{
                color: "white",
                fontSize: !isMobile ? 18 : 16,
                fontWeight: "bold",
                textAlign: "center",
                numberOfLines: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {name} invited you to join their team
            </Typography>
            {team.logo && (
              <CardMedia
                component="img"
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 2,
                  objectFit: "cover",
                }}
                image={team.logo}
                alt="Team logo"
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const getMetaTitle = () => {
    if (!team) return "Try Hooper today - download from the app store";
    const name = (user && getUsername(user)) || "Someone";
    const teamName = team.name || "their team";
    return `${name} invited you to join ${teamName} on Hooper`;
  };

  const getMetaDescription = () => {
    if (!team) return "Create your stats and highlights, shot by shot";
    const name = (user && getUsername(user)) || "someone";
    const teamName = team.name || "their team";
    return `Join ${name}'s team "${teamName}" on Hooper and start tracking your basketball stats and highlights together.`;
  };

  const getMetaImage = () => {
    if (team?.logo) return team.logo;
    if (user?.profilePicture) return user.profilePicture;
    return "/logos/logo-white.svg";
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: "100vh",
        flexDirection: "column",
        display: "flex",
        px: "0 !important",
      }}
    >
      <DynamicMeta
        title={getMetaTitle()}
        description={getMetaDescription()}
        image={getMetaImage()}
        type="profile"
        url={`/hoopers/team/${teamId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
      />
      <Box sx={WrapperStyles}>
        <Box style={{ marginTop: 100 }} />
        {loading ? (
          <LoadingOverlay />
        ) : (
          <DownloadOverlay
            parentViewName="Team"
            backgroundId="background-5.jpg"
            deepLinkPath={`/hoopers/team/${teamId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
            children={<TeamOverlay />}
          />
        )}
        <Box sx={{ my: 8 }} />
        <FooterOverlay />
      </Box>
    </Container>
  );
};

export default PublicTeamView;
